import * as React from 'react'

export type IconSize = 'xsmall' | 'small' | 'medium' | 'large' | number

export type IconProps = Omit<React.SVGAttributes<SVGElement>, 'children'> & {
  /**
   * Size of the icon
   * @default 'small'
   */
  size?: IconSize
}

const sizeMapping: Record<Exclude<IconSize, number>, number> = {
  xsmall: 12,
  small: 16,
  medium: 24,
  large: 40,
}

export const computeSize = (size: IconSize = 'small'): number => {
  if (typeof size === 'number') {
    return size
  }
  return sizeMapping[size]
}

type SvgContentProps = {
  color: string
}

/**
 * Factory function to create icon components with consistent boilerplate.
 * Reduces duplication across icon files by handling size computation,
 * ref forwarding, and common SVG attributes.
 */
export const createIcon = (
  SvgContent: React.FC<SvgContentProps>,
  viewBox: string,
  displayName: string,
): React.ForwardRefExoticComponent<
  IconProps & React.RefAttributes<SVGSVGElement>
> => {
  const Icon = React.forwardRef<SVGSVGElement, IconProps>(
    ({ size = 'small', className, color = 'currentColor', ...props }, ref) => {
      const computedSize = computeSize(size)

      return (
        <svg
          viewBox={viewBox}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={className}
          aria-hidden="true"
          width={computedSize}
          height={computedSize}
          ref={ref}
          {...props}
        >
          <SvgContent color={color} />
        </svg>
      )
    },
  )

  Icon.displayName = displayName

  return Icon
}
